import { Box, List, ListItem } from '@material-ui/core';
import { Input } from '../FormInputs';
import React, { useState, useRef } from 'react';
import DropdownPopover from '../DropdownPopover';
import PropTypes from 'prop-types';
import styles from './autoComplete.module.css';
import { ReactComponent as ChevronIcon } from 'images/at_chevron-down.svg';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { highlightMatch } from 'utils/utilities';
import usePaginatedOptions from 'hooks/usePaginatedOptions';
import cn from 'classnames';

const AutoComplete = ({
    label,
    name,
    value,
    placeholder,
    options = [],
    onChange,
    onSelect,
    error,
    helperText,
    disabled,
    loading = false,
    serverSideFiltering = false,
    disableCustomValue = false,
}) => {
    const inputRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchValue, setSearchValue] = useState('');

    const isOpen = Boolean(anchorEl);

    const { visibleOptions, onLoadMore, hasMore } = usePaginatedOptions({
        options,
        searchValue,
        batchSize: 20,
        disableFilter: serverSideFiltering,
    });

    const { loadingRef } = useInfiniteScroll({
        loadMore: onLoadMore,
        hasMore,
        isLoading: loading,
    });

    const handleInputClick = () => {
        setAnchorEl(inputRef.current);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchValue('');
    };

    const handleChange = (e) => {
        setSearchValue(e.target.value);
        // In pick-from-list-only mode, typing filters the list but is never committed as
        // the field value, so an unmatched entry can't linger and look like a valid selection.
        if (!disableCustomValue) {
            onChange(e);
        }
    };

    const handleSelectItem = (option) => {
        setSearchValue('');
        onSelect(option);
        handleClose();
    };

    const renderOptions = () => {
        if (loading) {
            return (
                <ListItem className={styles.autoCompleteListItem} disabled>
                    Loading...
                </ListItem>
            );
        }
        if (visibleOptions.length > 0) {
            return visibleOptions.map((option) => (
                <ListItem
                    button
                    key={option.value}
                    onClick={() => handleSelectItem(option)}
                    className={cn(styles.autoCompleteListItem, {
                        [styles.selected]: value === option.value,
                    })}
                >
                    {highlightMatch(option.label, searchValue)}
                </ListItem>
            ));
        }
        return (
            <ListItem className={styles.autoCompleteListItem} disabled>
                No options found
            </ListItem>
        );
    };

    return (
        <Box>
            <Input
                label={label}
                name={name}
                value={disableCustomValue && isOpen ? searchValue : value}
                onChange={handleChange}
                onClick={handleInputClick}
                placeholder={placeholder}
                error={error}
                helperText={helperText}
                disabled={disabled}
                EndAdornmentIcon={ChevronIcon}
                inputContainerRef={inputRef}
            />
            <DropdownPopover
                open={Boolean(anchorEl) && options.length > 0}
                setOpen={handleClose}
                anchorEl={inputRef.current}
                className={styles.autoCompleteDropdown}
            >
                <List>
                    {renderOptions()}
                    {!loading && hasMore && <div ref={loadingRef} />}
                </List>
            </DropdownPopover>
        </Box>
    );
};

AutoComplete.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            id: PropTypes.string,
        })
    ).isRequired,
    onSelect: PropTypes.func.isRequired,
    error: PropTypes.string,
    helperText: PropTypes.string,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    serverSideFiltering: PropTypes.bool,
    disableCustomValue: PropTypes.bool,
};

export default AutoComplete;
